import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAnswerQuestion from "./useAnswerQuestion";
import { convertByteArray, formatFileOnInput, isValidImageExt, resizeFile } from "../../common/attachments/attachmentsHelper";
import { ALERT_TYPES, showAlert } from "../../../redux/alert/alertActions";
import { removeFileFromArray, updateFileInArray } from "../utils/attachmentUtils";
import { fileMessages } from "../../admin/attachments/messages";

const useFileQuestionManager = (question, followUpUUID, maxNumberOfFiles, fileSizeLimit, allowedFileExtensions = []) => {
    const answerQuestion = useAnswerQuestion();
    const dispatch = useDispatch();
    const userProfile = useSelector((state) => state.login.userProfile);
    const limitForImageResize = fileSizeLimit / 2; // Arbitrary limit to avoid resizing small images
    const maxFileSizeDisplay = question?.props?.fileSize?.display || "20MB";
    const handleFileChange = useCallback(
        async (e) => {
            const { files } = e.target;
            if (files && files.length > 0) {
                const formattedFiles = question.value || [];
                if (question?.value?.length >= maxNumberOfFiles) {
                    dispatch(showAlert(Date.now(), fileMessages.description, ALERT_TYPES.error));
                    return;
                }
                for (let file of files) {
                    const fileExt = file.name.includes(".") ? `.${file.name.split(".").pop()}` : "";
                    if (file.type.includes("video")) {
                        dispatch(showAlert(Date.now(), fileMessages.NoAcceptableFileError, ALERT_TYPES.error));
                        continue;
                    }
                    if (!fileExt || !isValidImageExt(fileExt, allowedFileExtensions)) {
                        dispatch(showAlert(Date.now(), fileMessages.NoAcceptableFileError, ALERT_TYPES.error));
                        continue;
                    }
                    if (file.size > fileSizeLimit) {
                        dispatch(showAlert(Date.now(), fileMessages.fileTooLargeError, ALERT_TYPES.error, { size: maxFileSizeDisplay }));
                        continue;
                    }
                    if (file.type.includes("image") && file.size < fileSizeLimit && file.size > limitForImageResize) {
                        file = await resizeFile(file);
                    }
                    const data = await convertByteArray(file);
                    //TODO TECHNO-7828: figure out how to handle these values on the complete API story:

                    // When we need to have the parent job's clickDispatchId and workOrderNumber when we upload the files to the api.
                    // haven't decided if we should pass them as params to this function, get them from redux, or add them in later when we submit.
                    // Passing them in as params would cause the questionMapper to have hard coded values or pass them in for all questions
                    // Using redux within this hook is probably the cleanest solution, but im not sure if we have the parents job in state for all uses of this hook (eg. list view)
                    // We could also add them in later when we submit, but that would require changing the saga, and ensuring we have the data there as well.
                    // For now, just using temp values to get it working.
                    const clickDispatchId = "temp";
                    const workOrderNumber = "temp";
                    var formattedFile = formatFileOnInput(file, data, clickDispatchId, workOrderNumber, userProfile.username);
                    formattedFiles.push(formattedFile);
                }
                answerQuestion(formattedFiles, question, followUpUUID);
            }
        },
        [dispatch, answerQuestion, question, followUpUUID],
    );

    const handleFileUpdate = useCallback(
        (updatedFile) => {
            const updatedFileArray = updateFileInArray(question.value, updatedFile);
            answerQuestion(updatedFileArray, question, followUpUUID);
        },
        [answerQuestion, question, followUpUUID],
    );

    const handleRemoveFile = useCallback(
        (id) => {
            const updatedFileArray = removeFileFromArray(question.value, id);
            answerQuestion(updatedFileArray, question, followUpUUID);
        },
        [answerQuestion, question, followUpUUID],
    );

    return { handleFileChange, handleFileUpdate, handleRemoveFile };
};

export default useFileQuestionManager;
